/* ───────────────────────────────────────────────────────────────────────────
 * lcsMinimalAdapter.js
 *
 * Pure-JS adapter: converts a `decisionCard` (from LCS_DECISION_CARDS or
 * LCS_GENERATED_CARDS) plus its derived `ledger` and live `freshness` into
 * the minimal profile shape that MinimalDecisionPanel renders.
 *
 * This lets the cockpit serve the minimal "operator intelligence console"
 * format for ALL leases, not just the 5 hand-authored ones — without
 * regenerating a separate JSON file.
 *
 * Resolution order in the cockpit:
 *   1. MINIMAL_LCS_DECISION_PROFILES[leaseId]        (hand-authored, top 5)
 *   2. adaptDecisionCardToMinimal(card, ledger, ...) (every other lease)
 *   3. fallback DecisionCard (only if no card at all)
 * ──────────────────────────────────────────────────────────────────────── */

// ── Helpers ────────────────────────────────────────────────────────────────

function _modeFromStatus(statusKey) {
  const s = String(statusKey || "").toUpperCase();
  if (/RESOLVED|OPTIMIZATION CANDIDATE/.test(s))           return "OPTIMIZE";
  if (/INVESTIGATE|GAS HANDLING|VERIFY LINE|DECISION REQUIRED/.test(s)) return "INVESTIGATE";
  if (/DATA QUALITY|MEASUREMENT/.test(s))                  return "DATA QUALITY";
  if (/POST-WORKOVER|POST-RIG|WATCH \(NEED CONTEXT\)/.test(s)) return "REVIEW";
  if (/MONITOR|WATCH/.test(s))                             return "MONITOR";
  return "MONITOR";
}

function _whyForMode(mode) {
  switch (mode) {
    case "OPTIMIZE":     return "Stable producer; cost lever is the live opportunity.";
    case "INVESTIGATE":  return "Field issue likely; resolve root cause before sizing economics.";
    case "DATA QUALITY": return "Recent reads contaminated; restore data integrity before deciding.";
    case "REVIEW":       return "Recent intervention; let the well re-baseline before optimization.";
    case "MONITOR":      return "No active issue; routine monitoring cadence.";
    default:             return null;
  }
}

function _toneFromVerdict(v) {
  const s = String(v || "").toLowerCase();
  if (/improv|reversed|stabilized|refresh|recover|10×/.test(s))               return "positive";
  if (/escal|recur|chronic|stuck|active|leak|down|mechanic|stale|verify/.test(s)) return "negative";
  if (/resolv|settl|tight|stable|no change/.test(s))                          return "neutral";
  if (/caution|noisy|elevated|rising|but/.test(s))                            return "caution";
  return "neutral";
}

function _isCannotSize(card) {
  const lever = card?.moneyLever || {};
  const blob = `${lever.upside || ""} ${lever.label || ""}`.toLowerCase();
  return (
    !lever.upside ||
    /cannot size|no active|no production|asset classification needed|verify data state/.test(blob)
  );
}

function _hasBeforeAfter(c) {
  return c.before && c.before !== "—" && c.before !== "-" && c.before !== "";
}

// ── Inline proof: one short line synthesized from the strongest 2 changes ──
function _buildInlineProof(card) {
  const chips = (card.changeSummary || [])
    .filter(c => c.label && c.after && c.after !== "—" && c.after !== "-")
    .slice(0, 2);
  if (!chips.length) return null;
  return chips
    .map(c => {
      const val = _hasBeforeAfter(c) ? `${c.before}→${c.after}` : c.after;
      return `${c.label} ${val}`;
    })
    .join(" · ");
}

// ── Evidence cards: derive from changeSummary (preferred) or productionSignals
function _buildEvidenceCards(card) {
  const fromChanges = (card.changeSummary || []).slice(0, 3).map(c => ({
    title: c.label,
    main: _hasBeforeAfter(c) ? `${c.before} → ${c.after}` : c.after,
    detail: c.verdict ? c.verdict.charAt(0).toUpperCase() + c.verdict.slice(1) + "." : null,
    tone: _toneFromVerdict(c.verdict),
  }));
  if (fromChanges.length) return fromChanges;

  // Fallback: build from production signals if no change-summary chips.
  const ps = (card.productionSignals || []).slice(0, 3).map(p => ({
    title: String(p.stream).toUpperCase(),
    main: p.value,
    detail: p.signal && p.signal !== "n/a" ? p.signal.charAt(0).toUpperCase() + p.signal.slice(1) : null,
    tone: /clean|stable|recover/i.test(p.signal) ? "positive" :
          /volatile|leak|down|chronic/i.test(p.signal) ? "negative" : "neutral",
  }));
  return ps;
}

function _detailDrawer(card, ledger) {
  const production = (card.productionSignals || [])
    .filter(p => p.value && p.value !== "no series")
    .map(p => `${String(p.stream).toUpperCase()}: ${p.value}${p.signal && p.signal !== "n/a" ? ` (${p.signal})` : ""}`)
    .join(" · ");

  const notes = (card.evidence || []).join(" · ");

  let ledgerText = null;
  if (ledger?.actionStatus) {
    const status = ledger.actionStatus.status;
    const recheck = ledger.actualOutcomeWindow?.recheckAfterDays;
    const outcome = (ledger.actualOutcomeWindow?.status || "pending").toLowerCase();
    ledgerText = `${status}${recheck ? ` · recheck ${recheck}d` : ""} · outcome ${outcome}.`;
  }

  return {
    production: production || null,
    notes: notes || null,
    ledger: ledgerText,
  };
}

/**
 * Convert a synthesized/curated decisionCard + ledger into the minimal
 * profile shape rendered by MinimalDecisionPanel.
 *
 * Returns null only if the card is missing.
 */
export function adaptDecisionCardToMinimal({
  leaseId,
  leaseName,
  card,
  ledger,
  freshness,
  priority,
  confidence,
} = {}) {
  if (!card) return null;

  const mode = _modeFromStatus(card.status);
  const cantSize = _isCannotSize(card);
  const nextActions = card.nextActions || [];

  return {
    leaseId,
    leaseName: leaseName || leaseId,
    mode,
    priority: priority || card.priority || "Low",
    confidence: confidence || card.confidence || "Medium",
    freshness: freshness?.status || "Fresh",

    primaryMove: card.primaryMove,
    diagnosisLine: card.decisionLine,
    whyItMatters: _whyForMode(mode),
    inlineProof: _buildInlineProof(card),

    stakes: {
      value: cantSize ? "Cannot size yet" : (card.moneyLever?.upside || null),
      lever: card.moneyLever?.label || null,
      caveat: card.moneyLever?.caveat || null,
    },

    evidenceCards: _buildEvidenceCards(card),

    nextMove: {
      primary:   nextActions[0] || null,
      secondary: nextActions[1] || null,
      recheck:   ledger?.actualOutcomeWindow?.recheckAfterDays
        ? `${ledger.actualOutcomeWindow.recheckAfterDays}d`
        : null,
    },

    detailDrawer: _detailDrawer(card, ledger),
  };
}
